import React, { createContext, useState, useContext, ReactNode } from 'react';
import { toast, Toaster } from 'sonner';

interface ErrorContextType {
  setError: (message: string) => void;
  clearError: () => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const setError = (message: string) => {
    console.error('Error occurred:', message);
    toast.error(message, {
      duration: 5000,
      position: 'top-right',
    });
  };

  const clearError = () => {
    // With Sonner, we don't need to manually clear errors
    // as they automatically disappear after the duration
  };

  const showSuccess = (message: string) => {
    toast.success(message, {
      duration: 3000,
      position: 'top-right',
    });
  };

  const showInfo = (message: string) => {
    toast.info(message, {
      duration: 3000,
      position: 'top-right',
    });
  };

  const showWarning = (message: string) => {
    toast.warning(message, {
      duration: 4000,
      position: 'top-right',
    });
  };

  return (
    <ErrorContext.Provider value={{ 
      setError, 
      clearError, 
      showSuccess, 
      showInfo, 
      showWarning 
    }}>
      <Toaster richColors closeButton />
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}; 